import { useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useHideElements } from '@editorHooks'
import { getPositionGrouping } from '@queries'
import { usePersistentParams } from 'src/hooks'
import { EXPORT_FILTER_POSITION_GROUPING_TYPES } from '../constants'
import { PositionGroupingsByType, computeNonExportedPositionGuids } from '../utils'

const loadExportFilterGroupings = async (projectId: string): Promise<PositionGroupingsByType> => {
  const bundles = await Promise.all(
    EXPORT_FILTER_POSITION_GROUPING_TYPES.map(type => getPositionGrouping.request(projectId, type)),
  )
  const groupings: PositionGroupingsByType = {}
  EXPORT_FILTER_POSITION_GROUPING_TYPES.forEach((type, index) => {
    groupings[type] = bundles[index] as MemberPositionBundle[] | null
  })
  return groupings
}

/**
 * Hides the positions the position plan export leaves out (members whose position bundle is not
 * exported), so the scene preview shows exactly the exported positions. Loads the position
 * groupings the export consults in one go (refetched on every enter, since the exported flags are
 * edited on the results page); nothing is hidden until they are available or while `enabled` is
 * false.
 */
const useHideNonExportedPositions = (enabled: boolean): void => {
  const { projectId } = usePersistentParams() as { projectId?: string }
  const { enqueueSnackbar } = useSnackbar()
  const model = useModelStore(state => state.model)

  const { data: groupings } = useQuery({
    queryKey: ['getPlanViewExportFilterGroupings', projectId],
    queryFn: () => loadExportFilterGroupings(projectId as string),
    enabled: enabled && !!projectId,
    // The exported flags are edited on the results page, which does not touch this combined
    // query: always refetch when the page (re)mounts or the filter is switched on. Overrides the
    // client defaults (`staleTime: Infinity`, `refetchOnMount: false`).
    staleTime: 0,
    refetchOnMount: 'always',
    onError: (error: unknown) =>
      enqueueSnackbar(
        buildErrorMessage(
          error as AxiosError,
          'Fehler beim Laden der Positionsgruppierung für die Planansicht',
        ),
        { variant: 'error' },
      ),
  })

  const hiddenGuids = useMemo(() => {
    if (!enabled || !groupings) return null
    return computeNonExportedPositionGuids(model, groupings)
  }, [enabled, groupings, model])

  useHideElements(hiddenGuids)
}

export default useHideNonExportedPositions
